import React, {useContext, useEffect, useState, useRef, useImperativeHandle} from 'react';
import {Autocomplete, TextField, Typography, InputAdornment, IconButton} from "@mui/material";
import { X } from '@phosphor-icons/react';
import {AuthContext} from "../../auth/providers/AuthProvider";
import {CsrfContext} from "../../../providers/CsrfProvider";
import {listUserGroups} from "../../../services/group/Groups";
import {ThemeContext} from "styled-components";
import InputWithActions from "./Inputs/InputWithActions";

// Special marker object for "New Group" option
const NEW_GROUP_OPTION = { id: '__new_group__', name: '__new_group__', is_new_group: true };
// Special marker object for "Default Group" option (bills without a group_id)
const DEFAULT_GROUP_OPTION = { id: '__default_group__', name: '__default_group__', is_default_group: true };

const GroupSelector = React.forwardRef(({
    value,
    onChange,
    disabled = false,
    showCreateOption = true,
    filterOwnedOnly = false,
    menuContainer = null,
    onInputValueChange = null,
    skipWrapper = false,
    sx = {}
}, ref) => {
    const { authClaims } = useContext(AuthContext);
    const { csrfToken } = useContext(CsrfContext);
    const theme = useContext(ThemeContext);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const [isNewGroupMode, setIsNewGroupMode] = useState(false);
    const [clearedForNewGroup, setClearedForNewGroup] = useState(false); // After "+ New Group" click, show empty input (don't show Default Group)
    const [lastSelectedGroup, setLastSelectedGroup] = useState(null); // Track last selected group to preserve display
    const inputRef = useRef(null);

    // Expose input value via ref
    useImperativeHandle(ref, () => ({
        getInputValue: () => inputValue
    }), [inputValue]);

    useEffect(() => {
        if (authClaims && csrfToken) {
            loadGroups();
        }
    }, [authClaims, csrfToken]);

    // Set input value when value prop changes (e.g., when a group is selected)
    // This syncs the input value with the value prop from parent
    useEffect(() => {
        if (isNewGroupMode) {
            // Don't override input value when in new group mode
            return;
        }
        if (clearedForNewGroup) {
            // After "+ New Group" we intentionally show empty input; don't overwrite with "Default Group"
            return;
        }
        // Treat null/undefined/empty as "Default Group" (bills without a group)
        if (value === null || value === undefined || value === '') {
            setInputValue('Default Group');
        } else if (value) {
            const selectedGroup = groups.find(g => String(g.id) === String(value));
            if (selectedGroup) {
                // Display the group's actual name
                setInputValue(selectedGroup.name);
            }
            // If group not found, keep current input value (might be loading)
        }
    }, [value, groups, isNewGroupMode, clearedForNewGroup]);

    const loadGroups = async () => {
        try {
            setLoading(true);
            const groupsData = await listUserGroups(csrfToken);
            // listUserGroups should return groups the user owns OR participates in
            // Filter out archived groups
            const activeGroups = (groupsData || []).filter(group => !group.archived_at);
            setGroups(activeGroups);
        } catch (error) {
            console.error('Error loading groups:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (event, newInputValue, reason) => {
        // Update input when user types or clears
        // Don't update on 'reset' reason (when Autocomplete resets after selection)
        if (reason === 'input' || reason === 'clear') {
            setInputValue(newInputValue);
            // When user clears the input (e.g. select-all + delete), keep it empty; don't let sync effect restore "Default Group"
            if (!newInputValue || !String(newInputValue).trim()) {
                setClearedForNewGroup(true);
                if (onChange) {
                    onChange({ target: { name: 'group_id', value: null } });
                    onChange({ target: { name: 'new_group_name', value: '' } });
                }
            }
            // Notify parent of input value change
            if (onInputValueChange) {
                onInputValueChange(newInputValue);
            }
            // If in new group mode, update the new_group_name
            if (isNewGroupMode && onChange) {
                const syntheticEvent = {
                    target: {
                        name: 'new_group_name',
                        value: newInputValue.trim()
                    }
                };
                onChange(syntheticEvent);
            }
        }
        // Ignore 'reset' reason - we want to keep the input value we set in handleChange
    };

    const handleChange = (event, selectedValue) => {
        if (!selectedValue) {
            // Clear selection (user cleared input or clicked clear) — keep input empty, don't show "Default Group"
            setClearedForNewGroup(true);
            setIsNewGroupMode(false);
            if (onChange) {
                const syntheticEvent = {
                    target: {
                        name: 'group_id',
                        value: null
                    }
                };
                onChange(syntheticEvent);
                // Also clear new_group_name
                const clearNewGroupEvent = {
                    target: {
                        name: 'new_group_name',
                        value: ''
                    }
                };
                onChange(clearNewGroupEvent);
            }
            setInputValue('');
            return;
        }

        // Check if "Default Group" option was selected
        if (selectedValue === DEFAULT_GROUP_OPTION || selectedValue.is_default_group) {
            setClearedForNewGroup(false);
            setIsNewGroupMode(false);
            setInputValue('Default Group');
            if (onChange) {
                // Set group_id to null to represent bills without a group (default group)
                const syntheticEvent = {
                    target: {
                        name: 'group_id',
                        value: null
                    }
                };
                onChange(syntheticEvent);
                // Clear new_group_name
                const clearNewGroupEvent = {
                    target: {
                        name: 'new_group_name',
                        value: ''
                    }
                };
                onChange(clearNewGroupEvent);
            }
            return;
        }

        // Removed special handling for default group - treat it like any other group

        // Handle string values (when freeSolo allows typing new values)
        if (typeof selectedValue === 'string') {
            // User typed a new group name that doesn't match any existing group
            setClearedForNewGroup(false);
            setIsNewGroupMode(true);
            setInputValue(selectedValue);
            if (onChange) {
                // Clear group_id and set new_group_name
                const syntheticEvent = {
                    target: {
                        name: 'group_id',
                        value: null
                    }
                };
                onChange(syntheticEvent);
                const newGroupEvent = {
                    target: {
                        name: 'new_group_name',
                        value: selectedValue.trim()
                    }
                };
                onChange(newGroupEvent);
            }
            return;
        }

        // "+ New Group" selected: two flows
        if (selectedValue === NEW_GROUP_OPTION || (selectedValue.is_new_group)) {
            const onlyNewGroup = isOnlyNewGroupOption(inputValue, activeGroups);
            if (onlyNewGroup) {
                // Input doesn't match any group → treat as "create new group with this name"; keep text
                setClearedForNewGroup(false);
                setIsNewGroupMode(true);
                const name = inputValue.trim();
                if (onChange) {
                    onChange({ target: { name: 'group_id', value: null } });
                    onChange({ target: { name: 'new_group_name', value: name } });
                }
                return;
            }
            // Input was empty or matched default/existing group → clear and type new name; no dropdown while typing
            setClearedForNewGroup(true);
            setIsNewGroupMode(true);
            setInputValue('');
            if (onChange) {
                onChange({ target: { name: 'group_id', value: null } });
                onChange({ target: { name: 'new_group_name', value: '' } });
            }
            setTimeout(() => {
                const input = inputRef.current;
                if (input && typeof input.focus === 'function') {
                    input.focus();
                }
            }, 0);
            return;
        }

        // User selected an existing group
        if (selectedValue && selectedValue.id && !selectedValue.is_new_group) {
            setClearedForNewGroup(false);
            setIsNewGroupMode(false);
            // Store the selected group to preserve it even if groups array hasn't updated
            setLastSelectedGroup(selectedValue);
            // Set input value to the group's name immediately
            const groupName = selectedValue.name || '';
            setInputValue(groupName);
            if (onChange) {
                const syntheticEvent = {
                    target: {
                        name: 'group_id',
                        value: selectedValue.id
                    }
                };
                onChange(syntheticEvent);
                // Clear new_group_name
                const clearNewGroupEvent = {
                    target: {
                        name: 'new_group_name',
                        value: ''
                    }
                };
                onChange(clearNewGroupEvent);
            }
        }
    };

    // Filter out archived groups for the selector (only show active groups)
    const activeGroups = (groups || []).filter(group => !group.archived_at);

    // Same logic as filterOptions: returns true if the only option that would show is "+ New Group"
    const isOnlyNewGroupOption = (inputVal, groupList) => {
        const inputLower = (inputVal || '').toLowerCase().trim();
        if (inputLower.length === 0) return false;
        if ('default group'.includes(inputLower)) return false;
        const hasMatchingGroup = (groupList || []).some(g => (g.name || '').toLowerCase().includes(inputLower));
        return !hasMatchingGroup;
    };

    // Build options list: "Default Group" first (frontend construct), then groups, then "New Group" at the bottom if showCreateOption is true
    const options = showCreateOption 
        ? [DEFAULT_GROUP_OPTION, ...activeGroups, NEW_GROUP_OPTION] 
        : [DEFAULT_GROUP_OPTION, ...activeGroups];

    // Get the selected group object for Autocomplete value
    const selectedGroup = (() => {
        if (isNewGroupMode) {
            return null; // Don't show a selected value when in new group mode
        }
        if (value === null || value === undefined || value === '') {
            // After "+ New Group" we show empty input; otherwise show "Default Group"
            if (clearedForNewGroup) {
                return null;
            }
            return DEFAULT_GROUP_OPTION;
        }
        // Find the matching group
        const foundGroup = activeGroups.find(g => String(g.id) === String(value));
        // If not found in activeGroups but we have lastSelectedGroup with matching ID, use it
        if (!foundGroup && lastSelectedGroup && String(lastSelectedGroup.id) === String(value)) {
            return lastSelectedGroup;
        }
        return foundGroup || null;
    })();

    const newGroupTextField = (
        <TextField
            inputRef={inputRef}
            disabled={disabled || loading}
            label="Group"
            placeholder="Type new group name"
            value={inputValue}
            onChange={(e) => {
                const v = e.target.value;
                setInputValue(v);
                if (onInputValueChange) onInputValueChange(v);
                if (onChange) {
                    onChange({ target: { name: 'new_group_name', value: v.trim() } });
                }
            }}
            InputLabelProps={{ style: { color: theme.colors.borderContrast } }}
            InputProps={{
                style: { color: theme.colors.borderContrast },
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            size="small"
                            onClick={() => {
                                setIsNewGroupMode(false);
                                setClearedForNewGroup(true); // Keep input empty (don't show "Default Group")
                                setInputValue('');
                                if (onChange) {
                                    onChange({ target: { name: 'group_id', value: null } });
                                    onChange({ target: { name: 'new_group_name', value: '' } });
                                }
                                setTimeout(() => {
                                    inputRef.current?.focus?.();
                                }, 0);
                            }}
                            sx={{ color: theme.colors.borderContrast }}
                            aria-label="Back to group selector"
                        >
                            <X size={18} weight="bold" />
                        </IconButton>
                    </InputAdornment>
                ),
            }}
            sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                    color: theme.colors.borderContrast,
                    fontSize: '16px',
                    '& fieldset': { borderColor: theme.colors.borderContrast },
                    '&:hover fieldset': { borderColor: theme.colors.borderContrast },
                    '&.Mui-focused fieldset': { borderColor: theme.colors.borderContrast },
                },
                '& .MuiInputLabel-root': {
                    color: theme.colors.borderContrast,
                    fontSize: '16px',
                    '&.Mui-focused': { color: theme.colors.borderContrast },
                },
                '& .MuiInputBase-input': { fontSize: '16px' },
                ...sx,
            }}
        />
    );

    const autocompleteComponent = (
        <Autocomplete
                freeSolo={true}
                options={options}
                getOptionLabel={(option) => {
                    if (typeof option === 'string') {
                        return option;
                    }
                    if (option === NEW_GROUP_OPTION || option.is_new_group) {
                        return '+ New Group';
                    }
                    if (option === DEFAULT_GROUP_OPTION || option.is_default_group) {
                        return 'Default Group';
                    }
                    // Use the group's actual name
                    return option.name || '';
                }}
                isOptionEqualToValue={(option, value) => {
                    // Handle New Group option matching
                    if ((option === NEW_GROUP_OPTION || option?.is_new_group) && 
                        (value === NEW_GROUP_OPTION || value?.is_new_group)) {
                        return true;
                    }
                    // Handle Default Group option matching
                    if ((option === DEFAULT_GROUP_OPTION || option?.is_default_group) && 
                        (value === DEFAULT_GROUP_OPTION || value?.is_default_group)) {
                        return true;
                    }
                    // Handle Default Group matching with null value (bills without a group)
                    if ((option === DEFAULT_GROUP_OPTION || option?.is_default_group) && 
                        (value === null || value === undefined || value === '')) {
                        return true;
                    }
                    if ((value === DEFAULT_GROUP_OPTION || value?.is_default_group) && 
                        (option === null || option === undefined || option === '')) {
                        return true;
                    }
                    // Handle regular group matching by ID
                    if (option?.id && value?.id) {
                        return String(option.id) === String(value.id);
                    }
                    return false;
                }}
                value={selectedGroup || null}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                onChange={handleChange}
                loading={loading}
                disabled={disabled}
                filterOptions={(options, params) => {
                    const inputLower = params.inputValue.toLowerCase().trim();
                    
                    // Check for exact match (excluding special options)
                    const exactMatch = options.find((option) => {
                        if (option === NEW_GROUP_OPTION || option.is_new_group) return false;
                        if (option === DEFAULT_GROUP_OPTION || option.is_default_group) return false;
                        const name = option.name?.toLowerCase();
                        return name === inputLower;
                    });
                    
                    const filtered = options.filter((option) => {
                        // Always show "Default Group" option
                        if (option === DEFAULT_GROUP_OPTION || option.is_default_group) {
                            return 'default group'.includes(inputLower);
                        }
                        // Always show "+ new group" option when showCreateOption is true
                        if (option === NEW_GROUP_OPTION || option.is_new_group) {
                            return true;
                        }
                        // Use the group's actual name
                        const name = option.name || '';
                        return name.toLowerCase().includes(inputLower);
                    });
                    return filtered;
                }}
                renderOption={(props, option) => {
                    if (option === NEW_GROUP_OPTION || option.is_new_group) {
                        return (
                            <li {...props} key="__new_group__">
                                <Typography sx={{ fontStyle: 'italic' }}>
                                    + New Group
                                </Typography>
                            </li>
                        );
                    }
                    if (option === DEFAULT_GROUP_OPTION || option.is_default_group) {
                        return (
                            <li {...props} key="__default_group__">
                                <Typography sx={{ fontWeight: 600 }}>
                                    Default Group
                                </Typography>
                            </li>
                        );
                    }
                    // Use the group's actual name
                    return (
                        <li {...props} key={option.id}>
                            <Typography sx={{ fontWeight: 400 }}>
                                {option.name}
                            </Typography>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        inputRef={(el) => {
                            inputRef.current = el;
                            if (params.inputRef) {
                                if (typeof params.inputRef === 'function') {
                                    params.inputRef(el);
                                } else {
                                    params.inputRef.current = el;
                                }
                            }
                        }}
                        label="Group"
                        placeholder={isNewGroupMode ? "Type new group name" : "Select group"}
                        InputLabelProps={{
                            style: { color: theme.colors.borderContrast },
                        }}
                        InputProps={{
                            ...params.InputProps,
                            style: { color: theme.colors.borderContrast },
                            onBlur: (e) => {
                                setClearedForNewGroup(false); // Revert to showing "Default Group" when empty if user leaves field
                                params.InputProps.onBlur?.(e);
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: theme.colors.borderContrast,
                                fontSize: '16px', // Minimum 16px to prevent iOS zoom
                                '& fieldset': {
                                    borderColor: theme.colors.borderContrast,
                                },
                                '&:hover fieldset': {
                                    borderColor: theme.colors.borderContrast,
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: theme.colors.borderContrast,
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: theme.colors.borderContrast,
                                fontSize: '16px', // Minimum 16px to prevent iOS zoom
                                '&.Mui-focused': {
                                    color: theme.colors.borderContrast,
                                },
                            },
                            '& .MuiInputBase-input': {
                                fontSize: '16px', // Minimum 16px to prevent iOS zoom
                            },
                        }}
                    />
                )}
                PopperProps={{
                    placement: 'bottom-start',
                    ...(menuContainer ? { container: menuContainer } : { disablePortal: true }),
                }}
                sx={{ width: '100%', ...sx }}
            />
    );

    const mainInput = isNewGroupMode ? newGroupTextField : autocompleteComponent;

    return skipWrapper ? (
        mainInput
    ) : (
        <InputWithActions
            disabled={disabled || loading}
            inputType="text"
            forceSpacing={true}
            fullWidth
        >
            {mainInput}
        </InputWithActions>
    );
});

GroupSelector.displayName = 'GroupSelector';

export default GroupSelector;
